import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import toast, { ToastPosition } from 'react-hot-toast'
import cn from 'utils/cn'
import { ID } from 'types/entity'
import { AnimatePresence, motion } from 'framer-motion'
import Icon from 'core/components/icons/Icon'
import MotusIcon from 'core/components/motus-icons/MotusIcons'

type NotificationConfigProps = {
  title: ReactNode
  subtitle?: ReactNode
  duration?: number
  showClose?: boolean
  className?: string
  id?: string
  footer?: (dismiss: VoidFunction) => ReactNode
  position?: ToastPosition
  minimize?: boolean
}

export type NotificationProps = string | NotificationConfigProps

type Types = 'success' | 'error' | 'warning' | 'info' | 'loading'

const IconTypes: Record<string, ReactNode> = {
  success: <Icon name="check-circle" size={18} color="text-primary mt-0.5" />,
  error: (
    <Icon name="close-circle-outline" size={18} color="text-danger mt-0.5" />
  ),
  warning: (
    <MotusIcon name="warning-circle" size={20} color="text-yellow-600 mt-0.5" />
  ),
  loading: <Icon name="loading" size={18} color="text-dark-4 mt-0.5" />,
  info: null
}

interface BodyProps extends NotificationConfigProps {
  newId: string
  title: ReactNode
  icon: ReactNode
  dismiss: (id: ID) => void
}

const NotificationBody = React.memo(
  ({
    icon,
    title,
    subtitle,
    footer,
    showClose,
    dismiss,
    newId,
    minimize
  }: BodyProps) => {
    const [expanded, setExpanded] = useState(true)
    const minimizeIcon = useMemo(() => {
      if (!minimize) return null

      if (expanded) {
        return (
          <Icon
            name="minus"
            className="text-dark-4 text-xl hover:text-dark-3 cursor-pointer transition-colors mt-0.5"
            onClick={() => {
              setExpanded(false)
            }}
          />
        )
      }

      return (
        <Icon
          name="expand"
          onClick={() => {
            setExpanded(true)
          }}
          className="text-dark-4 text-lg hover:text-dark-3 cursor-pointer transition-colors mt-1"
        />
      )
    }, [expanded, minimize])

    const closeIcon = useMemo(() => {
      if (minimize || !showClose) return null

      return (
        <Icon
          name="close"
          size={18}
          color="text-dark-4 hover:text-dark-3 cursor-pointer transition-colors mt-0.5"
          onClick={() => {
            dismiss(newId)
          }}
        />
      )
    }, [dismiss, minimize, newId, showClose])

    return (
      <AnimatePresence initial={false}>
        <div>
          <span className="flex justify-between gap-4 w-full">
            <span className="flex items-start gap-2 w-full">
              {icon}
              <p className="font-medium	text-lg w-full">{title}</p>
            </span>
            {closeIcon}
            {minimizeIcon}
          </span>
          {expanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {subtitle && <p className="mt-3">{subtitle}</p>}
              {footer && (
                <span className="mt-8 flex align-middle justify-end">
                  {footer(() => dismiss(newId))}
                </span>
              )}
            </motion.div>
          )}
        </div>
      </AnimatePresence>
    )
  }
)

const useNotifications = () => {
  const dismissAll = useCallback(() => {
    toast.dismiss()
  }, [])

  const dismiss = useCallback((id: ID) => {
    toast.dismiss(id.toString())
  }, [])

  const show = useCallback(
    (props: NotificationProps, type: Types) => {
      const icon = IconTypes[type]
      const isSticky = type === 'error' || type === 'warning'

      const newProps =
        typeof props === 'string'
          ? { title: props, duration: isSticky ? Infinity : 3000 }
          : {
              ...props,
              duration: isSticky
                ? (props?.duration ?? Infinity)
                : (props?.duration ?? 3000)
            }
      const {
        title,
        subtitle,
        duration,
        showClose = true,
        footer,
        className,
        id: propId,
        position,
        minimize
      } = newProps
      const customId = propId || undefined

      return toast(
        ({ id: toastId }) => {
          const newId = customId ?? toastId
          return (
            <NotificationBody
              newId={newId}
              minimize={minimize}
              title={title}
              subtitle={subtitle}
              showClose={isSticky ? true : showClose}
              footer={footer}
              icon={icon}
              dismiss={dismiss}
            />
          )
        },
        {
          id: customId,
          duration: duration,
          className: cn(['min-w-[13rem] overflow-x-hidden', className]),
          position
        }
      )
    },
    [dismiss]
  )

  const success = useCallback(
    (props: NotificationProps) => {
      return show(props, 'success')
    },
    [show]
  )

  const error = useCallback(
    (props: NotificationProps) => {
      return show(props, 'error')
    },
    [show]
  )

  const warning = useCallback(
    (props: NotificationProps) => {
      return show(props, 'warning')
    },
    [show]
  )

  const info = useCallback(
    (props: NotificationProps) => {
      return show(props, 'info')
    },
    [show]
  )

  const loading = useCallback(
    (props: NotificationProps) => {
      return show(props, 'loading')
    },
    [show]
  )

  return {
    success,
    error,
    warning,
    info,
    loading,
    dismissAll,
    dismiss
  }
}

export default useNotifications
